import { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, blobErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AccessBlocked } from "@/components/AccessBlocked";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, ChevronRight, ChevronDown, ArrowLeft } from "lucide-react";

const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function CampaignReportPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const isAdmin = (user?.roles || []).some(r => ["Admin", "admin"].includes(r)) || perms.includes("admin.full_access") || perms.includes("leads.admin");
  const canView = isAdmin || perms.includes("leads.reports");

  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", campaign: "", source: "" });
  const [open, setOpen] = useState({});
  const [drill, setDrill] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    api.get(`/lead-reports/campaign?${p}`)
      .then(r => { setData(r.data); setBlocked(null); })
      .catch(e => {
        if ([401, 403, 410].includes(e.response?.status)) setBlocked(formatApiError(e.response?.data?.detail));
        else toast.error(formatApiError(e.response?.data?.detail));
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (canView) load(); else setLoading(false); }, []); // eslint-disable-line

  const openLeads = async (campaign, ad_set = "", ad_name = "") => {
    try {
      const p = new URLSearchParams({ campaign, ad_set, ad_name });
      if (filters.date_from) p.set("date_from", filters.date_from);
      if (filters.date_to) p.set("date_to", filters.date_to);
      const r = await api.get(`/lead-reports/campaign/leads?${p}`);
      setDrill({ title: [campaign, ad_set, ad_name].filter(Boolean).join(" · "), leads: r.data || [] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportXlsx = async () => {
    try {
      const p = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
      const r = await api.get(`/lead-reports/campaign/export?${p}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "orbit_campaign_report.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(await blobErrorMessage(e, "Export failed")); }
  };

  if (!canView) return <div className="p-6"><AccessBlocked detail="leads.reports required" testid="campaign-report-blocked" /></div>;
  if (blocked) return <div className="p-6"><AccessBlocked detail={blocked} testid="campaign-report-blocked" /></div>;

  const cols = ["Leads", "Contacted", "Qualified", "DP applications", "DP approved", "Customers", "Orders", "Order value", "Lead→order %"];
  const cells = (m) => [m.leads, m.contacted, m.qualified, m.dp_started, m.dp_approved, m.customers, m.orders, money(m.order_value), `${m.lead_to_order_pct}%`];

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="campaign-report-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/leads")}><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">Campaign report</h1>
            <p className="text-xs text-zinc-400 mt-0.5">Every imported Meta lead, from first contact to the order it became</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={exportXlsx} data-testid="export-report-btn"><Download className="w-3.5 h-3.5 mr-1" /> Export</Button>
      </div>

      <div className="border border-zinc-200 rounded-sm p-3 mb-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
        <div><Label className="text-xs text-zinc-500">From (lead date)</Label><Input type="date" className="h-8 text-xs mt-1" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} data-testid="filter-from" /></div>
        <div><Label className="text-xs text-zinc-500">To</Label><Input type="date" className="h-8 text-xs mt-1" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} data-testid="filter-to" /></div>
        <div>
          <Label className="text-xs text-zinc-500">Campaign</Label>
          <Select value={filters.campaign || "__all__"} onValueChange={v => setFilters(f => ({ ...f, campaign: v === "__all__" ? "" : v }))}>
            <SelectTrigger className="h-8 text-xs mt-1" data-testid="filter-campaign"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All campaigns</SelectItem>
              {(data?.campaigns || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-zinc-500">Source</Label>
          <Select value={filters.source || "__all__"} onValueChange={v => setFilters(f => ({ ...f, source: v === "__all__" ? "" : v }))}>
            <SelectTrigger className="h-8 text-xs mt-1" data-testid="filter-source"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All sources</SelectItem>
              {(data?.sources || []).map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" className="h-8 text-xs" onClick={load} data-testid="apply-filters-btn">Apply</Button>
      </div>

      {loading ? <p className="text-xs text-zinc-400 py-8 text-center">Loading...</p> : (
        <div className="border border-zinc-200 rounded-sm overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead><tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="text-left px-2 py-2 font-medium text-zinc-600">Campaign / Ad set / Ad</th>
              {cols.map(c => <th key={c} className="text-right px-2 py-2 font-medium text-zinc-600">{c}</th>)}
            </tr></thead>
            <tbody>
              {(data?.rows || []).length === 0 && (
                <tr><td colSpan={cols.length + 1} className="text-center text-zinc-400 py-6">No campaign leads in this range.</td></tr>
              )}
              {(data?.rows || []).map(r => (
                <Fragment key={r.campaign}>
                  <tr className="border-b border-zinc-100 bg-white" data-testid={`campaign-row-${r.campaign}`}>
                    <td className="px-2 py-1.5 font-medium text-zinc-800">
                      <button onClick={() => setOpen(o => ({ ...o, [r.campaign]: !o[r.campaign] }))} className="inline-flex items-center gap-1" data-testid={`expand-${r.campaign}`}>
                        {open[r.campaign] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} {r.campaign}
                      </button>
                    </td>
                    {cells(r).map((v, i) => (
                      <td key={i} className="px-2 py-1.5 text-right text-zinc-700">
                        {i === 0 ? <button className="underline" onClick={() => openLeads(r.campaign)} data-testid={`drill-${r.campaign}`}>{v}</button> : v}
                      </td>
                    ))}
                  </tr>
                  {open[r.campaign] && r.children.map(c => (
                    <tr key={`${r.campaign}-${c.ad_set}-${c.ad_name}`} className="border-b border-zinc-50 bg-zinc-50/50" data-testid={`ad-row-${c.ad_set}-${c.ad_name}`}>
                      <td className="px-2 py-1.5 pl-7 text-zinc-600">{c.ad_set || "—"} › {c.ad_name || "—"}</td>
                      {cells(c).map((v, i) => (
                        <td key={i} className="px-2 py-1.5 text-right text-zinc-600">
                          {i === 0 ? <button className="underline" onClick={() => openLeads(r.campaign, c.ad_set, c.ad_name)}>{v}</button> : v}
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
              {data?.other_sources && (
                <tr className="border-t border-zinc-200 bg-zinc-50" data-testid="other-sources-row">
                  <td className="px-2 py-1.5 text-zinc-500 italic">{data.other_sources.campaign}</td>
                  {cells(data.other_sources).map((v, i) => (
                    <td key={i} className="px-2 py-1.5 text-right text-zinc-500">
                      {i === 0 ? <button className="underline" onClick={() => openLeads("__other__")} data-testid="drill-other">{v}</button> : v}
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {drill && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-6" onClick={() => setDrill(null)}>
          <div className="bg-white rounded-sm border border-zinc-200 max-w-3xl w-full max-h-[80vh] overflow-auto p-4" onClick={e => e.stopPropagation()} data-testid="drill-panel">
            <h3 className="text-sm font-semibold text-zinc-900 mb-2">{drill.title} — {drill.leads.length} lead(s)</h3>
            <table className="w-full text-[11px]">
              <thead><tr className="bg-zinc-50 border-b border-zinc-200">
                {["Name", "Phone", "City", "Status", "Lead date"].map(h => <th key={h} className="text-left px-2 py-1.5 font-medium text-zinc-600">{h}</th>)}
              </tr></thead>
              <tbody>
                {drill.leads.map(l => (
                  <tr key={l._id} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer" onClick={() => navigate(`/leads/${l._id}`)} data-testid={`drill-lead-${l._id}`}>
                    <td className="px-2 py-1.5 text-zinc-800">{l.name}</td>
                    <td className="px-2 py-1.5 font-mono text-zinc-600">{l.phone || "—"}</td>
                    <td className="px-2 py-1.5 text-zinc-600">{l.city || "—"}</td>
                    <td className="px-2 py-1.5 text-zinc-600">{(l.status || "").replace(/_/g, " ")}</td>
                    <td className="px-2 py-1.5 text-zinc-500">{l.lead_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end mt-3"><Button size="sm" variant="outline" onClick={() => setDrill(null)} data-testid="close-drill">Close</Button></div>
          </div>
        </div>
      )}
    </div>
  );
}
